import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { usePermissao } from '../../hooks/usePermissao';
import Sino from './Sino';
import logoJatear from '../../assets/logo_jatear.png';
import styles from './Layout.module.css';

// Ícones SVG inline (evita dependência de biblioteca)
const Icons = {
  Dashboard: () => (
    <svg className={styles.navIcon} viewBox="0 0 16 16" fill="none">
      <rect x="1" y="1" width="6" height="6" rx="1" fill="currentColor" />
      <rect x="9" y="1" width="6" height="6" rx="1" fill="currentColor" />
      <rect x="1" y="9" width="6" height="6" rx="1" fill="currentColor" />
      <rect x="9" y="9" width="6" height="6" rx="1" fill="currentColor" />
    </svg>
  ),
  Movimentacao: () => (
    <svg className={styles.navIcon} viewBox="0 0 16 16" fill="none">
      <path d="M8 2v12M3 7l5-5 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 14h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  Estoque: () => (
    <svg className={styles.navIcon} viewBox="0 0 16 16" fill="none">
      <rect x="1" y="3" width="14" height="2" rx="1" fill="currentColor" />
      <rect x="1" y="7" width="14" height="2" rx="1" fill="currentColor" />
      <rect x="1" y="11" width="10" height="2" rx="1" fill="currentColor" />
    </svg>
  ),
  OS: () => (
    <svg className={styles.navIcon} viewBox="0 0 16 16" fill="none">
      <rect x="3" y="1" width="10" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M6 5h4M6 8h4M6 11h2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  Cadastros: () => (
    <svg className={styles.navIcon} viewBox="0 0 16 16" fill="none">
      <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="4" cy="4" r="1" fill="currentColor" />
      <circle cx="8" cy="8" r="1" fill="currentColor" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
    </svg>
  ),
  Relatorios: () => (
    <svg className={styles.navIcon} viewBox="0 0 16 16" fill="none">
      <path d="M2 11l3-3 3 3 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Configuracoes: () => (
    <svg className={styles.navIcon} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 5.5v3l1.5 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
};

// Menu mapeia cada item para a acao da matriz central (usePermissao.js).
// Para ajustar quem ve o que, edite APENAS a matriz no hook.
const menu = [
  { to: '/dashboard', label: 'Início', Icon: Icons.Dashboard, acao: 'verDashboard' },
  { to: '/movimentacao', label: 'Movimentação', Icon: Icons.Movimentacao, acao: 'verMovimentacao' },
  { to: '/estoque', label: 'Estoque', Icon: Icons.Estoque, acao: 'verEstoque' },
  { to: '/os', label: 'OS', Icon: Icons.OS, acao: 'verOS' },
  { to: '/cadastros', label: 'Cadastros', Icon: Icons.Cadastros, acao: 'verCadastros' },
  { to: '/relatorios', label: 'Relatórios', Icon: Icons.Relatorios, acao: 'verRelatorios' },
];

export default function Layout() {
  const navigate = useNavigate();
  const usuario = useAuthStore((s) => s.usuario);
  const perfil = useAuthStore((s) => s.perfil);
  const logout = useAuthStore((s) => s.logout);
  const { pode } = usePermissao();

  const [sidebarAberta, setSidebarAberta] = useState(false);

  const inicial = (usuario?.displayName || usuario?.email || 'U')
    .charAt(0)
    .toUpperCase();

  const nomeExibicao = usuario?.displayName ||
    (usuario?.email ? usuario.email.split('@')[0].split('.')[0] : 'Usuário');

  function fecharSidebar() {
    setSidebarAberta(false);
  }

  async function handleLogout() {
    fecharSidebar();
    await logout();
    navigate('/login');
  }

  return (
    <div className={styles.appShell}>
      {/* Topbar mobile — oculto no desktop via CSS */}
      <div className={styles.mobileTopbar}>
        <button
          className={styles.hamburger}
          onClick={() => setSidebarAberta(true)}
          aria-label="Abrir menu"
        >
          ☰
        </button>
        <span className={styles.mobileTopbarTitle}>Gestão de Tintas</span>
        <div style={{ width: 40 }} />
      </div>

      {/* Overlay escuro — só visível no mobile quando sidebar aberta */}
      <div
        className={`${styles.mobileOverlay} ${sidebarAberta ? styles.mobileOverlayAberto : ''}`}
        onClick={fecharSidebar}
        aria-hidden="true"
      />

      <nav className={`${styles.sidebar} ${sidebarAberta ? styles.sidebarAberta : ''}`}>
        <div className={styles.sysBand}>Gestão de Tintas</div>

        <div className={styles.sidebarLogo}>
          <img src={logoJatear} alt="Jatear" />
        </div>

        <div className={styles.nav}>
          {menu
            .filter((item) => pode(item.acao))
            .map(({ to, label, Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={fecharSidebar}
                className={({ isActive }) =>
                  `${styles.navItem} ${isActive ? styles.active : ''}`
                }
              >
                <Icon />
                {label}
              </NavLink>
            ))}
        </div>

        <div className={styles.navBottom}>
          <Sino />
          {pode('verConfiguracoes') && (
            <NavLink
              to="/configuracoes"
              onClick={fecharSidebar}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.active : ''}`
              }
            >
              <Icons.Configuracoes />
              Configurações
            </NavLink>
          )}
          <div className={styles.navUser} onClick={handleLogout} title="Clique para sair">
            <div className={styles.navAvatar}>{inicial}</div>
            <div>
              <div className={styles.navUname} style={{ textTransform: 'capitalize' }}>
                {nomeExibicao}
              </div>
              <div className={styles.navUrole}>{perfil || 'Usuário'}</div>
            </div>
          </div>
        </div>
      </nav>

      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}
